import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { BadgeCheck, Download, Edit3, Mail, Plus, Trash2 } from "lucide-react";
import {
  DEFAULT_GENERAL_SUPPLIER,
  FORM_PAYMENT_METHODS,
  GeneratedGeneralInvoice,
  GeneralInvoiceCustomer,
  GeneralInvoiceLine,
  InvoicePaymentMethod,
  InvoicePaymentTerms,
  InvoiceSupplierDetails,
  SavedInvoiceClient,
  dueDateFor,
  formPaymentTermsValue,
  formatGbp,
  formatUkDate,
  invoicePaymentMethodLabel,
  invoicePaymentTermsLabel,
  invoiceTotals,
  isInvoicePaid,
  invoiceEmailLabel,
  invoicePaidOnLabel,
  invoicePaymentState,
  londonTodayYmd,
  normaliseInvoicePaymentMethod,
  paymentTermsFromDates,
  sanitiseInvoicePrefix,
  suggestInvoicePrefix,
  todayIsoDate,
} from "../utils/generalInvoiceTypes";
import {
  allocateGeneralInvoiceNumber,
  loadGeneralInvoiceVault,
  persistGeneralInvoiceVault,
  rebuildGeneralInvoiceSeq,
  GeneralInvoiceVault,
} from "../utils/generalInvoiceStorage";
import { downloadGeneralInvoicePdf } from "../utils/generalInvoicePdf";
import { askInvoiceRecipient, emailGeneralInvoicePdf, SPIRES_OFFICE_EMAIL } from "../utils/generalInvoiceEmail";

function emptyLine(): GeneralInvoiceLine {
  return {
    description: "Private hire journey",
    quantity: 1,
    unitPrice: 0,
    vatRatePercent: 0,
  };
}

function emptyCustomer(): GeneralInvoiceCustomer {
  return { name: "", address: "", email: "", phone: "" };
}

function emptyClientDraft() {
  return { name: "", invoicePrefix: "", email: "", phone: "", address: "" };
}

function newClientId(): string {
  return `client-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function pdfInputFromRecord(
  row: GeneratedGeneralInvoice,
  supplier: InvoiceSupplierDetails
) {
  return {
    invoiceNumber: row.invoiceNumber,
    invoiceDate: row.invoiceDate,
    dueDate: row.dueDate,
    paymentTerms: row.paymentTerms,
    customer: row.customer,
    bookingNumber: row.bookingNumber,
    travelDate: row.travelDate,
    pickup: row.pickup,
    destination: row.destination,
    lines: row.lines,
    notes: row.notes,
    supplier,
    paymentMethod: normaliseInvoicePaymentMethod(row.paymentMethod),
    paid: Boolean(row.paidAt),
    paidAt: row.paidAt,
  };
}

const GeneralInvoices: React.FC = () => {
  const [vault, setVault] = useState<GeneralInvoiceVault | null>(null);
  const [supplier, setSupplier] = useState<InvoiceSupplierDetails>(DEFAULT_GENERAL_SUPPLIER);
  const [clients, setClients] = useState<SavedInvoiceClient[]>([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientDraft, setClientDraft] = useState(emptyClientDraft);
  const [history, setHistory] = useState<GeneratedGeneralInvoice[]>([]);
  const [customer, setCustomer] = useState<GeneralInvoiceCustomer>(emptyCustomer);
  const [invoiceDate, setInvoiceDate] = useState(() => todayIsoDate());
  const [paymentTerms, setPaymentTerms] = useState<InvoicePaymentTerms>("due_on_receipt");
  const [paymentMethod, setPaymentMethod] = useState<InvoicePaymentMethod>("bank_transfer");
  const [dueDate, setDueDate] = useState(() => todayIsoDate());
  const [bookingNumber, setBookingNumber] = useState("");
  const [travelDate, setTravelDate] = useState("");
  const [pickup, setPickup] = useState("");
  const [destination, setDestination] = useState("");
  const [lines, setLines] = useState<GeneralInvoiceLine[]>([emptyLine()]);
  const [notes, setNotes] = useState("");
  const [paid, setPaid] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<"save" | "download" | "email" | "delete" | "paid" | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const loaded = await loadGeneralInvoiceVault();
        if (cancelled) return;
        setVault(loaded);
        setSupplier(loaded.supplier);
        setClients(loaded.clients);
        setHistory(loaded.invoices);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not load encrypted invoices from Firebase.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = async (next: GeneralInvoiceVault) => {
    const withClients = { ...next, clients: next.clients ?? clients };
    await persistGeneralInvoiceVault(withClients);
    setVault(withClients);
    setSupplier(withClients.supplier);
    setClients(withClients.clients);
    setHistory(withClients.invoices);
  };

  const totals = useMemo(() => invoiceTotals(lines), [lines]);
  const selectedClient = clients.find((row) => row.id === selectedClientId) || null;
  const editingInvoice = history.find((row) => row.id === editingId) || null;
  const activePrefix = sanitiseInvoicePrefix(
    selectedClient?.invoicePrefix || supplier.invoicePrefix || "ST-INV"
  );

  const handleInvoiceDateChange = (nextInvoiceDate: string) => {
    setInvoiceDate(nextInvoiceDate);
    if (formPaymentTermsValue(paymentTerms) === "custom") {
      setPaymentTerms(paymentTermsFromDates(nextInvoiceDate, dueDate));
      return;
    }
    setDueDate(dueDateFor(nextInvoiceDate, paymentTerms));
  };

  const handlePaymentTermsChange = (nextTerms: InvoicePaymentTerms) => {
    setPaymentTerms(nextTerms);
    if (nextTerms !== "custom") {
      setDueDate(dueDateFor(invoiceDate, nextTerms));
    }
  };

  const handleDueDateChange = (nextDueDate: string) => {
    setDueDate(nextDueDate);
    setPaymentTerms(paymentTermsFromDates(invoiceDate, nextDueDate));
  };

  const saveSupplier = async (e: FormEvent) => {
    e.preventDefault();
    if (!vault) return;
    setError("");
    try {
      await persist({ ...vault, supplier, clients });
      setStatus("Company details saved encrypted in Firebase.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save company details.");
    }
  };

  const applyClient = (client: SavedInvoiceClient) => {
    setSelectedClientId(client.id);
    setCustomer({
      name: client.name,
      address: client.address,
      email: client.email,
      phone: client.phone,
    });
    setStatus(
      editingId
        ? `Using ${client.name} details. Invoice number ${editingInvoice?.invoiceNumber || ""} stays the same.`
        : `Using ${client.name}. Invoice numbers will use prefix ${client.invoicePrefix}.`
    );
  };

  const saveClient = async (e: FormEvent) => {
    e.preventDefault();
    if (!vault) return;
    const name = clientDraft.name.trim();
    if (!name) {
      setError("Enter a client name to save.");
      return;
    }
    const invoicePrefix = sanitiseInvoicePrefix(clientDraft.invoicePrefix || suggestInvoicePrefix(name));
    const duplicate = clients.find((row) => sanitiseInvoicePrefix(row.invoicePrefix) === invoicePrefix);
    if (duplicate) {
      setError(`Prefix ${invoicePrefix} is already used by ${duplicate.name}.`);
      return;
    }
    setError("");
    const saved: SavedInvoiceClient = {
      id: newClientId(),
      name,
      address: clientDraft.address.trim(),
      email: clientDraft.email.trim(),
      phone: clientDraft.phone.trim(),
      invoicePrefix,
    };
    try {
      await persist({ ...vault, supplier, clients: [...clients, saved] });
      setClientDraft(emptyClientDraft());
      applyClient(saved);
      setStatus(`Saved ${saved.name}. Their invoices will be numbered ${saved.invoicePrefix}-YYYYMM-001.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save client.");
    }
  };

  const removeClient = async (id: string, name: string) => {
    if (!vault) return;
    const ok = window.confirm(`Remove saved client ${name}? Existing invoices are kept.`);
    if (!ok) return;
    try {
      const nextClients = clients.filter((row) => row.id !== id);
      await persist({ ...vault, supplier, clients: nextClients });
      if (selectedClientId === id) setSelectedClientId("");
      setStatus(`${name} removed from saved clients.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove client.");
    }
  };

  const updateLine = (index: number, patch: Partial<GeneralInvoiceLine>) => {
    setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  const validate = (): string | null => {
    if (!supplier.providerName.trim() || !supplier.address.trim() || !supplier.phone.trim() || !supplier.email.trim()) {
      return "Fill in headed-paper details: provider name, address, phone and email.";
    }
    if (!supplier.sortCode.trim() || !supplier.accountNumber.trim() || !supplier.accountName.trim()) {
      return "Bank account name, sort code and account number are required.";
    }
    if (!customer.name.trim()) {
      return "Enter the customer name.";
    }
    const usable = lines.filter((l) => l.description.trim() && l.quantity > 0 && l.unitPrice > 0);
    if (!usable.length) {
      return "Add at least one line with a description, quantity and unit price.";
    }
    if (totals.total <= 0) {
      return "Invoice total must be greater than zero.";
    }
    if (!invoiceDate) {
      return "Choose an invoice date.";
    }
    if (!dueDate) {
      return "Choose a due date.";
    }
    return null;
  };

  const resetCreateForm = () => {
    setEditingId(null);
    setSelectedClientId("");
    setCustomer(emptyCustomer());
    setInvoiceDate(todayIsoDate());
    setPaymentTerms("due_on_receipt");
    setPaymentMethod("bank_transfer");
    setDueDate(todayIsoDate());
    setBookingNumber("");
    setTravelDate("");
    setPickup("");
    setDestination("");
    setLines([emptyLine()]);
    setNotes("");
    setPaid(false);
  };

  const startEdit = (row: GeneratedGeneralInvoice) => {
    setEditingId(row.id);
    setSelectedClientId(
      row.clientId && clients.some((client) => client.id === row.clientId) ? row.clientId : ""
    );
    setCustomer({
      name: row.customer?.name || "",
      address: row.customer?.address || "",
      email: row.customer?.email || "",
      phone: row.customer?.phone || "",
    });
    const nextInvoiceDate = row.invoiceDate || todayIsoDate();
    const nextDueDate = row.dueDate || nextInvoiceDate;
    setInvoiceDate(nextInvoiceDate);
    setDueDate(nextDueDate);
    setPaymentTerms(paymentTermsFromDates(nextInvoiceDate, nextDueDate));
    setPaymentMethod(normaliseInvoicePaymentMethod(row.paymentMethod));
    setBookingNumber(row.bookingNumber || "");
    setTravelDate(row.travelDate || "");
    setPickup(row.pickup || "");
    setDestination(row.destination || "");
    setLines(
      Array.isArray(row.lines) && row.lines.length
        ? row.lines.map((line) => ({ ...line }))
        : [emptyLine()]
    );
    setNotes(row.notes || "");
    setPaid(isInvoicePaid(row));
    setError("");
    setStatus(`Editing ${row.invoiceNumber}. The invoice number stays the same.`);
    window.requestAnimationFrame(() => {
      document.getElementById("general-invoice-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const cancelEdit = () => {
    resetCreateForm();
    setError("");
    setStatus("Edit cancelled. New invoices will get the next number.");
  };

  const buildDraftRecord = (
    invoiceNumber: string,
    existing?: GeneratedGeneralInvoice
  ): GeneratedGeneralInvoice => {
    const usable = lines.filter((l) => l.description.trim() && l.quantity > 0 && l.unitPrice > 0);
    return {
      id: existing?.id || invoiceNumber,
      invoiceNumber,
      invoiceDate,
      dueDate,
      paymentTerms: paymentTermsFromDates(invoiceDate, dueDate),
      paymentMethod,
      customer: {
        name: customer.name.trim(),
        address: customer.address.trim(),
        email: customer.email.trim(),
        phone: customer.phone.trim(),
      },
      clientId: selectedClientId || undefined,
      bookingNumber: bookingNumber.trim(),
      travelDate,
      pickup: pickup.trim(),
      destination: destination.trim(),
      lines: usable,
      notes: notes.trim(),
      createdAt: existing?.createdAt || new Date().toISOString(),
      emailedAt: existing?.emailedAt,
      emailedTo: existing?.emailedTo,
      emailStatus: existing?.emailStatus,
      paidAt: paid ? existing?.paidAt || new Date().toISOString() : null,
    };
  };

  const persistInvoiceRecord = async (
    record: GeneratedGeneralInvoice,
    nextVault: GeneralInvoiceVault
  ) => {
    const invoices = editingId
      ? history.map((row) => (row.id === record.id ? record : row))
      : [record, ...history];
    await persist({ ...nextVault, supplier, clients, invoices });
    return invoices;
  };

  const handleSaveEdit = async () => {
    setError("");
    setStatus("");
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    if (!vault || !editingInvoice) {
      setError("Choose an invoice to edit first.");
      return;
    }
    setBusy("save");
    try {
      const record = buildDraftRecord(editingInvoice.invoiceNumber, editingInvoice);
      await persistInvoiceRecord(record, vault);
      setStatus(`Saved changes to ${record.invoiceNumber}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the invoice.");
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async () => {
    setError("");
    setStatus("");
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    if (!vault) {
      setError("Invoice vault is not loaded yet.");
      return;
    }
    setBusy("download");
    try {
      let record: GeneratedGeneralInvoice;
      let nextVault = vault;
      if (editingInvoice) {
        record = buildDraftRecord(editingInvoice.invoiceNumber, editingInvoice);
      } else {
        const allocated = allocateGeneralInvoiceNumber(vault, activePrefix, invoiceDate);
        record = buildDraftRecord(allocated.invoiceNumber);
        nextVault = allocated.vault;
      }
      await downloadGeneralInvoicePdf(pdfInputFromRecord(record, supplier));
      await persistInvoiceRecord(record, nextVault);
      setStatus(
        editingInvoice
          ? `Updated PDF ${record.invoiceNumber} downloaded.`
          : `PDF ${record.invoiceNumber} downloaded.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not generate the PDF.");
    } finally {
      setBusy(null);
    }
  };

  const markEmailed = async (
    invoices: GeneratedGeneralInvoice[],
    invoiceNumber: string,
    to: string,
    nextVault: GeneralInvoiceVault
  ) => {
    const stamped = invoices.map((row) =>
      row.invoiceNumber === invoiceNumber
        ? { ...row, emailedAt: new Date().toISOString(), emailedTo: to, emailStatus: "sent" as const, emailError: undefined }
        : row
    );
    await persist({ ...nextVault, supplier, invoices: stamped });
  };

  const handleEmail = async () => {
    setError("");
    setStatus("");
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    if (!vault) {
      setError("Invoice vault is not loaded yet.");
      return;
    }
    const to = askInvoiceRecipient(customer.email, paid);
    if (!to) return;
    setBusy("email");
    try {
      let record: GeneratedGeneralInvoice;
      let nextVault = vault;
      if (editingInvoice) {
        record = buildDraftRecord(editingInvoice.invoiceNumber, editingInvoice);
      } else {
        const allocated = allocateGeneralInvoiceNumber(vault, activePrefix, invoiceDate);
        record = buildDraftRecord(allocated.invoiceNumber);
        nextVault = allocated.vault;
      }
      record.customer = { ...record.customer, email: to };
      const invoices = await persistInvoiceRecord(record, nextVault);
      const sent = await emailGeneralInvoicePdf(record, supplier, to);
      await markEmailed(invoices, record.invoiceNumber, sent.to, { ...nextVault, invoices });
      setStatus(
        sent.cc
          ? `Invoice ${record.invoiceNumber} emailed to ${sent.to}, copy to ${sent.cc}.`
          : `Invoice ${record.invoiceNumber} emailed to ${sent.to}.`
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not email the invoice.";
      setError(message);
      if (editingInvoice && vault) {
        const failed = history.map((item) =>
          item.id === editingInvoice.id ? { ...item, emailStatus: "failed" as const, emailError: message } : item
        );
        try {
          await persist({ ...vault, supplier, clients, invoices: failed });
        } catch {
          /* the email error is already shown */
        }
      }
    } finally {
      setBusy(null);
    }
  };

  const emailSavedInvoice = async (
    row: GeneratedGeneralInvoice,
    invoicesSnapshot?: GeneratedGeneralInvoice[]
  ) => {
    if (!vault) return;
    const to = askInvoiceRecipient(row.customer.email, isInvoicePaid(row));
    if (!to) return;
    setError("");
    setStatus("");
    setBusy("email");
    const sourceInvoices = invoicesSnapshot || history;
    try {
      const sent = await emailGeneralInvoicePdf(row, supplier, to);
      await markEmailed(sourceInvoices, row.invoiceNumber, sent.to, { ...vault, invoices: sourceInvoices });
      setStatus(
        sent.cc
          ? `${isInvoicePaid(row) ? "Paid invoice" : "Invoice"} ${row.invoiceNumber} sent to ${sent.to}, copy to ${sent.cc}.`
          : `${isInvoicePaid(row) ? "Paid invoice" : "Invoice"} ${row.invoiceNumber} sent to ${sent.to}.`
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "Could not email the invoice.";
      const failed = sourceInvoices.map((item) =>
        item.invoiceNumber === row.invoiceNumber ? { ...item, emailStatus: "failed" as const, emailError: message } : item
      );
      try {
        await persist({ ...vault, supplier, clients, invoices: failed });
      } catch {
        /* keep the original error visible */
      }
      setError(message);
    } finally {
      setBusy(null);
    }
  };

  const toggleInvoicePaid = async (row: GeneratedGeneralInvoice, makePaid: boolean) => {
    if (!vault) return;
    setError("");
    setStatus("");
    setBusy("paid");
    let handedOffToEmail = false;
    try {
      const updated: GeneratedGeneralInvoice = {
        ...row,
        paidAt: makePaid ? row.paidAt || new Date().toISOString() : null,
      };
      const invoices = history.map((item) => (item.id === row.id ? updated : item));
      await persist({ ...vault, supplier, clients, invoices });
      if (editingId === row.id) setPaid(makePaid);
      if (!makePaid) {
        setStatus(`${row.invoiceNumber} marked as unpaid.`);
        return;
      }
      setStatus(`${row.invoiceNumber} marked as paid. You can email the paid PDF from the mail button.`);
      const sendNow = window.confirm(
        `${row.invoiceNumber} is marked as paid. Email the paid invoice to the customer now?`
      );
      if (sendNow) {
        handedOffToEmail = true;
        await emailSavedInvoice(updated, invoices);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update paid status.");
    } finally {
      if (!handedOffToEmail) setBusy(null);
    }
  };

  const removeGeneratedInvoice = async (id: string, invoiceNumber: string) => {
    if (!vault) return;
    const ok = window.confirm(
      `Delete ${invoiceNumber} from Firebase? Company details are kept. This cannot be undone.`
    );
    if (!ok) return;
    setError("");
    setBusy("delete");
    try {
      const invoices = history.filter((row) => row.id !== id);
      await persist({ ...vault, supplier, clients, invoices, seq: rebuildGeneralInvoiceSeq(invoices) });
      if (editingId === id) resetCreateForm();
      setStatus(`${invoiceNumber} removed. The next PDF can reuse that number if it was the latest.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete invoice.");
    } finally {
      setBusy(null);
    }
  };

  const redownloadInvoice = async (row: GeneratedGeneralInvoice) => {
    setError("");
    setStatus("");
    setBusy("download");
    try {
      await downloadGeneralInvoicePdf(pdfInputFromRecord(row, supplier));
      setStatus(`PDF ${row.invoiceNumber} downloaded again.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not download the PDF.");
    } finally {
      setBusy(null);
    }
  };

  const clearGeneratedInvoices = async () => {
    if (!vault || history.length === 0) return;
    const ok = window.confirm(
      `Delete all ${history.length} recently generated invoice${history.length === 1 ? "" : "s"} from Firebase? Company details are kept. Invoice numbers will start again from 001.`
    );
    if (!ok) return;
    setError("");
    setBusy("delete");
    try {
      await persist({ ...vault, supplier, clients, invoices: [], seq: {} });
      resetCreateForm();
      setStatus("Recently generated invoices cleared. The next PDF will be numbered 001.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not clear invoices.");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <p className="text-sm text-gray-600">Loading encrypted invoices from Firebase…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Invoices</h2>
        <p className="text-sm text-gray-600 mt-1">
          General customer invoices on Spires headed paper. Bank details, VAT numbers and invoice history are stored in Firebase as AES-256-GCM ciphertext — they are not readable in the Firestore console.
          Use School invoices for Oxfordshire County Council DPS work.
        </p>
      </div>

      <form className="card" onSubmit={saveSupplier}>
        <div className="card-header">
          <h3 className="card-title" style={{ fontSize: "1.15rem" }}>Headed paper and bank details</h3>
          <button type="submit" className="btn btn-primary btn-sm">Save details</button>
        </div>
        <p className="text-sm text-gray-600" style={{ marginTop: "-0.5rem", marginBottom: "1rem" }}>
          VAT number and company number are left blank until you enter the real values — do not guess them.
        </p>
        <div className="school-invoice-grid">
          <label className="form-group">
            <span className="form-label">Service provider name</span>
            <input className="form-input" value={supplier.providerName} onChange={(e) => setSupplier({ ...supplier, providerName: e.target.value })} />
          </label>
          <label className="form-group">
            <span className="form-label">Company registration number</span>
            <input className="form-input" value={supplier.companyNumber} onChange={(e) => setSupplier({ ...supplier, companyNumber: e.target.value })} placeholder="If registered" />
          </label>
          <label className="form-group" style={{ gridColumn: "1 / -1" }}>
            <span className="form-label">Company address</span>
            <textarea className="form-textarea" rows={3} value={supplier.address} onChange={(e) => setSupplier({ ...supplier, address: e.target.value })} placeholder="Required on headed paper" />
          </label>
          <label className="form-group">
            <span className="form-label">Phone</span>
            <input className="form-input" value={supplier.phone} onChange={(e) => setSupplier({ ...supplier, phone: e.target.value })} />
          </label>
          <label className="form-group">
            <span className="form-label">Email</span>
            <input className="form-input" value={supplier.email} onChange={(e) => setSupplier({ ...supplier, email: e.target.value })} />
          </label>
          <label className="form-group">
            <span className="form-label">VAT number</span>
            <input className="form-input" value={supplier.vatNumber} onChange={(e) => setSupplier({ ...supplier, vatNumber: e.target.value })} placeholder="Leave blank if not VAT registered" />
          </label>
          <label className="form-group">
            <span className="form-label">Default prefix for one-off customers</span>
            <input className="form-input" value={supplier.invoicePrefix} onChange={(e) => setSupplier({ ...supplier, invoicePrefix: e.target.value })} />
          </label>
          <label className="form-group">
            <span className="form-label">Account name</span>
            <input className="form-input" value={supplier.accountName} onChange={(e) => setSupplier({ ...supplier, accountName: e.target.value })} />
          </label>
          <label className="form-group">
            <span className="form-label">Sort code</span>
            <input className="form-input" value={supplier.sortCode} onChange={(e) => setSupplier({ ...supplier, sortCode: e.target.value })} />
          </label>
          <label className="form-group">
            <span className="form-label">Account number</span>
            <input className="form-input" value={supplier.accountNumber} onChange={(e) => setSupplier({ ...supplier, accountNumber: e.target.value })} />
          </label>
        </div>
      </form>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ fontSize: "1.15rem" }}>Saved clients</h3>
        </div>
        <p className="text-sm text-gray-600" style={{ marginTop: "-0.5rem", marginBottom: "1rem" }}>
          Each saved client has their own invoice prefix, so their numbers stay in a separate sequence.
        </p>
        <form onSubmit={saveClient} className="school-invoice-grid" style={{ marginBottom: "1rem" }}>
          <label className="form-group">
            <span className="form-label">Client name</span>
            <input
              className="form-input"
              value={clientDraft.name}
              onChange={(e) => {
                const name = e.target.value;
                setClientDraft((current) => ({
                  ...current,
                  name,
                  invoicePrefix:
                    !current.invoicePrefix || current.invoicePrefix === suggestInvoicePrefix(current.name)
                      ? suggestInvoicePrefix(name)
                      : current.invoicePrefix,
                }));
              }}
            />
          </label>
          <label className="form-group">
            <span className="form-label">Invoice prefix</span>
            <input
              className="form-input"
              value={clientDraft.invoicePrefix}
              onChange={(e) => setClientDraft({ ...clientDraft, invoicePrefix: e.target.value.toUpperCase() })}
              placeholder="ST-ACME"
            />
          </label>
          <label className="form-group">
            <span className="form-label">Email</span>
            <input className="form-input" type="email" value={clientDraft.email} onChange={(e) => setClientDraft({ ...clientDraft, email: e.target.value })} />
          </label>
          <label className="form-group">
            <span className="form-label">Phone</span>
            <input className="form-input" value={clientDraft.phone} onChange={(e) => setClientDraft({ ...clientDraft, phone: e.target.value })} />
          </label>
          <label className="form-group" style={{ gridColumn: "1 / -1" }}>
            <span className="form-label">Address</span>
            <textarea className="form-textarea" rows={2} value={clientDraft.address} onChange={(e) => setClientDraft({ ...clientDraft, address: e.target.value })} />
          </label>
          <div>
            <button type="submit" className="btn btn-primary btn-sm">
              <Plus className="h-4 w-4 mr-1" /> Save client
            </button>
          </div>
        </form>
        {clients.length === 0 ? (
          <p className="text-sm text-gray-500">No saved clients yet. Add a name and prefix, then use them on invoices.</p>
        ) : (
          <ul className="space-y-2">
            {clients.map((client) => (
              <li key={client.id} className="flex items-center justify-between gap-3" style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "0.65rem 0.85rem" }}>
                <div>
                  <strong>{client.name}</strong>
                  <span className="text-sm text-gray-600"> · {client.invoicePrefix}</span>
                  {client.email ? <div className="text-sm text-gray-500">{client.email}</div> : null}
                </div>
                <div className="flex gap-2">
                  <button type="button" className="btn btn-sm" onClick={() => applyClient(client)}>Use on invoice</button>
                  <button type="button" className="btn btn-sm" onClick={() => removeClient(client.id, client.name)} aria-label={`Remove client ${client.name}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card" id="general-invoice-form">
        <div className="card-header">
          <h3 className="card-title" style={{ fontSize: "1.15rem" }}>
            {editingInvoice ? `Edit invoice ${editingInvoice.invoiceNumber}` : "Create invoice"}
          </h3>
          {editingInvoice ? (
            <button type="button" className="btn btn-sm" onClick={cancelEdit} disabled={Boolean(busy)}>
              Cancel edit
            </button>
          ) : null}
        </div>
        {editingInvoice ? (
          <p className="text-sm text-gray-600" style={{ marginTop: "-0.5rem", marginBottom: "1rem" }}>
            Changes keep invoice number {editingInvoice.invoiceNumber}. Save, download, or email to update the stored invoice.
          </p>
        ) : null}
        <div className="school-invoice-grid">
          <label className="form-group">
            <span className="form-label">Saved client</span>
            <select
              className="form-select"
              value={selectedClientId}
              onChange={(e) => {
                const id = e.target.value;
                if (!id) {
                  setSelectedClientId("");
                  return;
                }
                const found = clients.find((row) => row.id === id);
                if (found) applyClient(found);
              }}
            >
              <option value="">One-off customer ({sanitiseInvoicePrefix(supplier.invoicePrefix || "ST-INV")})</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} ({client.invoicePrefix})
                </option>
              ))}
            </select>
          </label>
          <label className="form-group">
            <span className="form-label">{editingInvoice ? "Invoice number" : "Invoice prefix in use"}</span>
            <input className="form-input" value={editingInvoice ? editingInvoice.invoiceNumber : activePrefix} readOnly />
          </label>
          <label className="form-group">
            <span className="form-label">Invoice date</span>
            <input className="form-input" type="date" value={invoiceDate} onChange={(e) => handleInvoiceDateChange(e.target.value)} />
          </label>
          <label className="form-group">
            <span className="form-label">Payment terms</span>
            <select
              className="form-select"
              value={formPaymentTermsValue(paymentTerms)}
              onChange={(e) => handlePaymentTermsChange(e.target.value as InvoicePaymentTerms)}
            >
              <option value="due_on_receipt">Due on receipt</option>
              <option value="3_days">Payment within 3 days</option>
              <option value="7_days">Payment within 7 days</option>
              <option value="14_days">Payment within 14 days</option>
              <option value="30_days">Payment within 30 days</option>
              <option value="custom">Custom due date</option>
            </select>
          </label>
          <label className="form-group">
            <span className="form-label">Due date</span>
            <input className="form-input" type="date" value={dueDate} onChange={(e) => handleDueDateChange(e.target.value)} />
            <span className="text-xs text-gray-500" style={{ marginTop: "0.35rem" }}>
              Payment terms follow the due date. Choosing a standard term fills the due date for you.
            </span>
          </label>
          <label className="form-group">
            <span className="form-label">Pay by</span>
            <select
              className="form-select"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as InvoicePaymentMethod)}
            >
              {FORM_PAYMENT_METHODS.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-500" style={{ marginTop: "0.35rem" }}>
              {paymentMethod === "bank_transfer"
                ? "Bank details and the invoice number as the payment reference will appear on the PDF."
                : paymentMethod === "card"
                  ? "The invoice will ask the customer to pay by card."
                  : "The invoice will ask the customer to pay in cash."}
            </span>
          </label>
          <label className="form-group">
            <span className="form-label">Customer name</span>
            <input className="form-input" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
          </label>
          <label className="form-group">
            <span className="form-label">Customer email</span>
            <input className="form-input" type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} placeholder="Used when you email the invoice" />
          </label>
          <label className="form-group">
            <span className="form-label">Customer phone</span>
            <input className="form-input" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
          </label>
          <label className="form-group" style={{ gridColumn: "1 / -1" }}>
            <span className="form-label">Customer address</span>
            <textarea className="form-textarea" rows={3} value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} />
          </label>
          <label className="form-group">
            <span className="form-label">Booking reference (optional)</span>
            <input className="form-input" value={bookingNumber} onChange={(e) => setBookingNumber(e.target.value)} />
          </label>
          <label className="form-group">
            <span className="form-label">Travel date (optional)</span>
            <input className="form-input" type="date" value={travelDate} onChange={(e) => setTravelDate(e.target.value)} />
          </label>
          <label className="form-group">
            <span className="form-label">Pickup (optional)</span>
            <input className="form-input" value={pickup} onChange={(e) => setPickup(e.target.value)} />
          </label>
          <label className="form-group">
            <span className="form-label">Destination (optional)</span>
            <input className="form-input" value={destination} onChange={(e) => setDestination(e.target.value)} />
          </label>
        </div>

        {lines.map((line, index) => (
          <div key={index} className="school-invoice-line">
            <label className="form-group" style={{ gridColumn: "1 / -1" }}>
              <span className="form-label">Description</span>
              <input className="form-input" value={line.description} onChange={(e) => updateLine(index, { description: e.target.value })} />
            </label>
            <label className="form-group">
              <span className="form-label">Quantity</span>
              <input className="form-input" type="number" min="0" step="0.01" value={line.quantity || ""} onChange={(e) => updateLine(index, { quantity: Number(e.target.value) || 0 })} />
            </label>
            <label className="form-group">
              <span className="form-label">Unit price (£)</span>
              <input className="form-input" type="number" min="0" step="0.01" value={line.unitPrice || ""} onChange={(e) => updateLine(index, { unitPrice: Number(e.target.value) || 0 })} />
            </label>
            <label className="form-group">
              <span className="form-label">VAT %</span>
              <input className="form-input" type="number" min="0" step="0.1" value={line.vatRatePercent} onChange={(e) => updateLine(index, { vatRatePercent: Number(e.target.value) || 0 })} />
            </label>
            {lines.length > 1 && (
              <button type="button" className="btn btn-sm" onClick={() => setLines(lines.filter((_, i) => i !== index))}>Remove line</button>
            )}
          </div>
        ))}
        <button type="button" className="btn btn-sm" onClick={() => setLines([...lines, emptyLine()])}>
          <Plus className="h-4 w-4 mr-1" /> Add line
        </button>

        <label className="form-group" style={{ marginTop: "1.25rem" }}>
          <span className="form-label">Notes (optional)</span>
          <textarea className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Waiting time, extras, or payment notes" />
        </label>

        <label className="form-group" style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginTop: "0.75rem" }}>
          <input
            type="checkbox"
            checked={paid}
            onChange={(e) => setPaid(e.target.checked)}
          />
          <span className="form-label" style={{ margin: 0 }}>Paid — add the PAID stamp to the PDF</span>
        </label>

        <div className="school-invoice-totals">
          <div>Net total <strong>{formatGbp(totals.net)}</strong></div>
          <div>VAT total <strong>{formatGbp(totals.vat)}</strong></div>
          <div>Total payable <strong>{formatGbp(totals.total)}</strong></div>
        </div>

        {error && <p className="text-sm" style={{ color: "#b91c1c", marginBottom: "0.75rem" }}>{error}</p>}
        {status && <p className="text-sm" style={{ color: "#166534", marginBottom: "0.75rem" }}>{status}</p>}

        <div className="flex flex-wrap gap-2">
          {editingInvoice ? (
            <button type="button" className="btn btn-primary" onClick={handleSaveEdit} disabled={Boolean(busy)}>
              {busy === "save" ? "Saving…" : "Save changes"}
            </button>
          ) : null}
          <button type="button" className="btn btn-primary" onClick={handleDownload} disabled={Boolean(busy)}>
            <Download className="h-4 w-4 mr-2" />
            {busy === "download" ? "Preparing PDF…" : editingInvoice ? "Save and download PDF" : "Download PDF invoice"}
          </button>
          <button type="button" className="btn btn-success" onClick={handleEmail} disabled={Boolean(busy)}>
            <Mail className="h-4 w-4 mr-2" />
            {busy === "email" ? "Sending…" : paid ? (editingInvoice ? "Save and email paid PDF" : "Email paid invoice") : editingInvoice ? "Save and email PDF" : "Email PDF invoice"}
          </button>
        </div>
        <p className="text-xs text-gray-500" style={{ marginTop: "0.75rem" }}>
          Emails go to the customer address you confirm, with a copy to {SPIRES_OFFICE_EMAIL}.
        </p>
      </div>

      {history.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title" style={{ fontSize: "1.15rem" }}>Recently generated</h3>
            <button type="button" className="btn btn-danger btn-sm" onClick={clearGeneratedInvoices} disabled={Boolean(busy)}>
              Clear all
            </button>
          </div>
          <p className="text-sm text-gray-600" style={{ marginTop: "-0.5rem", marginBottom: "1rem" }}>
            Download again with the current headed paper, mark as paid to add the stamp, or email the PDF to the customer (copy to {SPIRES_OFFICE_EMAIL}).
          </p>
          <ul className="text-sm space-y-2">
            {history.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-3" style={{ border: editingId === row.id ? "2px solid #002147" : "1px solid #e5e7eb", borderRadius: 8, padding: "0.65rem 0.85rem" }}>
                <div>
                  <strong>{row.invoiceNumber}</strong>
                  {" · "}
                  {row.customer.name}
                  {" · "}
                  {formatUkDate(row.invoiceDate)}
                  {row.dueDate ? ` · due ${formatUkDate(row.dueDate)}` : ""}
                  {" · "}
                  {invoicePaymentTermsLabel(row.invoiceDate, row.dueDate)}
                  {" · "}
                  {invoicePaymentMethodLabel(row.paymentMethod)}
                  {" · "}
                  {formatGbp(invoiceTotals(row.lines).total)}
                  {(() => {
                    const pay = invoicePaymentState(row, londonTodayYmd());
                    const paidOn = invoicePaidOnLabel(row.paidAt);
                    const payLabel = pay === "paid" ? `Paid${paidOn ? ` · ${paidOn}` : ""}` : pay === "overdue" ? "Overdue" : "Unpaid";
                    const payColor = pay === "paid" ? "#166534" : pay === "overdue" ? "#9a3412" : "#374151";
                    return (
                      <>
                        <div className="text-xs" style={{ color: payColor, fontWeight: 600 }}>{payLabel}</div>
                        <div className="text-xs text-gray-500">{invoiceEmailLabel(row)}</div>
                      </>
                    );
                  })()}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => startEdit(row)}
                    disabled={Boolean(busy)}
                    title="Edit this invoice"
                    aria-label={`Edit invoice ${row.invoiceNumber}`}
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={() => redownloadInvoice(row)}
                    disabled={Boolean(busy)}
                    title="Download this invoice PDF again"
                    aria-label={`Redownload invoice ${row.invoiceNumber}`}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={() => toggleInvoicePaid(row, !isInvoicePaid(row))}
                    disabled={Boolean(busy)}
                    title={isInvoicePaid(row) ? "Remove paid stamp" : "Mark as paid and add the PAID stamp"}
                    aria-label={isInvoicePaid(row) ? `Mark invoice ${row.invoiceNumber} unpaid` : `Mark invoice ${row.invoiceNumber} paid`}
                    style={isInvoicePaid(row) ? { background: "#166534", borderColor: "#166534" } : undefined}
                  >
                    <BadgeCheck className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={() => emailSavedInvoice(row)}
                    disabled={Boolean(busy)}
                    title={isInvoicePaid(row) ? `Email paid invoice (copy to ${SPIRES_OFFICE_EMAIL})` : `Email this invoice (copy to ${SPIRES_OFFICE_EMAIL})`}
                    aria-label={isInvoicePaid(row) ? `Email paid invoice ${row.invoiceNumber}` : `Email invoice ${row.invoiceNumber}`}
                  >
                    <Mail className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeGeneratedInvoice(row.id, row.invoiceNumber)}
                    disabled={Boolean(busy)}
                    aria-label={`Delete invoice ${row.invoiceNumber}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GeneralInvoices;
